/* Core */
import { createContext, useContext, useEffect, useState } from 'react';

/* Real time data */
import Pusher from 'pusher-js';

const WebsocketContext = createContext({
	socketReady: false,
	user: null,
	setUser: () => {},
	subscribeToChannel: () => {},
	unsubscribeToChannel: () => {},
});

const EVENT_NAMESPACE = 'App\\Events\\';

const WebsocketProvider = ({ children }) => {
	const [user, setUser] = useState();
	const [socket, setSocket] = useState();

	useEffect(() => {
		return () => {
			socket?.disconnect();

			setSocket(undefined);
			setUser(undefined);
		};
	}, []);

	useEffect(() => {
		if (user) {
			if (!socket) {
				setSocket(createSocketConnection());
			}
		} else {
			socket?.disconnect();
			setSocket(undefined);
		}
	}, [user]);

	const subscribeToChannel = (channel, event, callback, isPrivate = false) => {
		console.log(`Subscribe to channel ${channel} and listen for ${event}`);

		if (!socket) {
			console.log('[subscribe]: No socket...');
			return;
		}

		if (
			socket.channels.find(channel)?.callbacks?._callbacks[
				`_${EVENT_NAMESPACE}${event}`
			]
		) {
			console.log('Event already exists on channel!');
			return;
		}

		socket
			.subscribe(`${isPrivate ? 'private-' : ''}${channel}`)
			.bind(`${EVENT_NAMESPACE}${event}`, callback);
	};

	const unsubscribeToChannel = (channel, event, isPrivate = false) => {
		console.log(`Unsubscribe from channel ${channel}`);

		if (!socket) {
			console.log('[unsubscribe]: No socket...');
			return;
		}

		if (event) {
			socket.unbind(`${EVENT_NAMESPACE}${event}`);
		}

		if (channel) {
			socket.unsubscribe(`${isPrivate ? 'private-' : ''}${channel}`);
		}
	};

	const value = {
		socketReady: Boolean(socket),
		user,
		setUser,
		subscribeToChannel,
		unsubscribeToChannel,
	};

	return (
		<WebsocketContext.Provider value={value}>
			{children}
		</WebsocketContext.Provider>
	);
};

const createSocketConnection = () => {
	console.log('Init socket...');

	console.log('Process: ', process);

	const socket = new Pusher(process.env.MIX_PUSHER_APP_KEY, {
		cluster: process.env.PUSHER_APP_CLUSTER ?? '',
		wsHost: process.env.MIX_PUSHER_APP_URL,
		wsPort: 6002,
		wsPath: '',
		disableStats: true,
		enabledTransports: ['ws'],
		forceTLS: false,
		userAuthentication: {
			endpoint: `${process.env.MIX_PUSHER_APP_AUTH_ENDPOINT}/pusher/auth`,
			transport: 'ajax',
			params: {},
			headers: {
				Authorization: `${localStorage.getItem('authToken')}`,
			},
			customHandler: undefined,
		},
		channelAuthorization: {
			endpoint: `${process.env.MIX_PUSHER_APP_AUTH_ENDPOINT}/pusher/auth`,
			transport: 'ajax',
			params: {},
			headers: {
				Authorization: `${localStorage.getItem('authToken')}`,
			},
			customHandler: undefined,
		},
	});

	return socket;
};

const useSocketAuth = () => {
	const state = useContext < WebsocketContextType > WebsocketContext;

	if (state === undefined) {
		throw new Error('useSocketAuth must be used within a WebsocketProvider');
	}

	return { setUser: state.setUser };
};

const useChannel = () => {
	const state = useContext < WebsocketContextType > WebsocketContext;

	if (state === undefined) {
		throw new Error(
			'useSubscription must be used within a WebsocketProvider',
		);
	}

	return {
		socketReady: state.socketReady,
		subscribe: state.subscribeToChannel,
		unsubscribe: state.unsubscribeToChannel,
	};
};

export { WebsocketProvider, useChannel, createSocketConnection, useSocketAuth };
